import _extends from 'babel-runtime/helpers/extends';
import _ from 'lodash';
import React from 'react';
import { v4 as uuidV4 } from 'uuid';
import { stopAllMedia } from '@rosetta/gaia-media';
import { pronunciationModel } from '../../step-pronunciation/pronunciation-model';
import { stepModel } from '../step-model';
import { COURSE_PROGRESS_UPDATED } from '../../events/redux/braze-reducer';
import { userSelectors } from '../../user/redux/user-reducer';
import { localizationModel } from '../../content-localization/localization-model';
import { progressModel, stepStatusModel } from '@rosetta/gaia-progress';
import { amplitudeHelper } from '../../events/amplitudeHelper';
import { stepMultipleChoiceModel } from '../../step-multiple-choice/step-multiple-choice-model';
import { withPlayer } from './withPlayer';

// Enhance handler with stopAll (media)
export var mediaStoppingHandler = function mediaStoppingHandler(handler) {
  return _.flow(stopAllMedia, handler);
};

var navigator = React.createRef();
/**
 * Player is a control layer above the step status.
 * Player provides both public and private interface for the step status for the step.
 * Further more, components may depend on player interface and be able to represent different player states.
 *
 * NOTES:  withStepPlayer depends on the wrappedComponent having a certain set of props:
 *
 *      // route has all the path info about this activity
 *      route: PropTypes.object.isRequired,
 *      course: PropTypes.object.isRequired,
 *      sequence: PropTypes.object.isRequired,
 *      activity: PropTypes.object.isRequired,
 *      step: PropTypes.object.isRequired,
 *      history: PropTypes.object.isRequired
 */
export var withStepPlayer = withPlayer(function (props) {
  return {
    get activityStepAttemptId() {
      return props.stepStatus.activityStepAttemptId;
    },

    get isTestSequence() {
      return props.isTestSequence;
    },

    get history() {
      return props.history;
    },

    get exitRoute() {
      return props.route.paths.course;
    },

    get values() {
      return props.stepStatus.values;
    },

    get answers() {
      return props.stepStatus.answers;
    },

    get completed() {
      return props.stepStatus.completed;
    },

    get correct() {
      return props.stepStatus.correct;
    },

    get review() {
      return props.stepStatus.review;
    },

    get ready() {
      return props.stepStatus.ready;
    },

    get attempts() {
      return props.stepStatus.attempts;
    },

    get skipped() {
      var stepStatus = props.stepStatus;

      return stepStatus.skipped;
    },

    get stepNumber() {
      return props.stepStatus.stepNumber;
    },

    get stepsCount() {
      return props.stepStatus.stepsCount;
    },

    get isLastStep() {
      return this.stepNumber === this.stepsCount;
    },

    get started() {
      return props.stepStatus.activityStepId === this.initialStepStatus.activityStepId;
    },

    get maxFailedAttempts() {
      var stepStatus = props.stepStatus;

      return stepStatus.attempts >= stepStatus.maxFailAttempts && !stepStatus.correct;
    },

    get readOnly() {
      var stepStatus = props.stepStatus;

      return stepStatus.completed || stepStatus.review;
    },

    get showAnswers() {
      return this.maxFailedAttempts && props.stepStatus.completed;
    },

    get showErrors() {
      var stepStatus = props.stepStatus;

      return stepStatus.review && !stepStatus.correct && !stepStatus.completed;
    },

    get feedback() {
      if (props.isTestSequence) {
        return '';
      }
      var sequenceProgress = props.sequenceProgress,
          stepStatus = props.stepStatus,
          step = props.step,
          content = props.content;

      var activityProgress = progressModel.getActivityProgress(sequenceProgress, props.activity.activityId);
      var completed = this.isLastStep && progressModel.activityIsComplete(activityProgress);

      if (this.showAnswers) {
        return ['_player_title_show_answers'];
      } else if (stepStatus.correct && !completed) {
        return ['_player_title_correct'];
      } else if (stepStatus.completed) {
        return completed ? ['_player_title_completed'] : '';
      } else if (stepStatus.review) {
        var answersCount = _.size(step.correct) + (_.get(step, 'behavior.firstIsExample') ? -1 : 0);

        var givenItems = content && content.filter(function (item) {
          return item.promptType === 'given';
        });
        if (givenItems && !!givenItems.length) {
          answersCount = answersCount - givenItems.length;
        }
        if (answersCount < 2) {
          return ['_player_title_incorrect'];
        }
        if (stepMultipleChoiceModel.hasMultipleAnswers(step.behavior)) {
          return ['_player_title_incorrect'];
        }
        var wrongAnswersCount = stepStatus.answers.filter(function (answer) {
          return !answer.correct;
        }).length;
        return ['_player_title_incorrect_n_of_n', {
          numberOfIncorrectAnswers: wrongAnswersCount,
          numberOfAnswers: answersCount
        }];
      }
      // returning something is required of a getter
      return '';
    },

    get title() {
      var step = props.step,
          languages = props.languages;

      return localizationModel.getLocalizedContentText(_.flatten(step.instructions), languages, true);
    },

    get description() {
      var languages = props.languages,
          activity = props.activity;

      if (!activity.titles) {
        return null;
      }
      return localizationModel.getLocalizedContentText(activity.titles, languages, true);
    },

    get instructions() {
      // Instructions are removed for the beta.
      // Once decided to get them back, define instructions here.
      return _.get(props, 'step.instructions');
    },

    get navigator() {
      return navigator;
    },

    get stepCounter() {
      var stepStatus = props.stepStatus;

      return stepStatus.stepsCount > 1;
    },

    /**
     * Actions define properties for buttons in the player footer.
     */
    get actions() {
      var actionDescriptor = void 0;
      if (this.isTestSequence) {
        return [this.actionDescriptors.submit];
      }
      if (this.completed) {
        actionDescriptor = this.actionDescriptors.completed;
      } else if (this.review) {
        // User is reviewing his mistakes
        actionDescriptor = this.maxFailedAttempts ? this.actionDescriptors.showAnswers : this.actionDescriptors.tryAgain;
      } else if (this.ready) {
        actionDescriptor = this.actionDescriptors.checkAnswer;
      } else {
        actionDescriptor = this.actionDescriptors.skip;
      }
      return [actionDescriptor];
    },

    // actionDescriptors member buttonText are each an array of the args to lionTranslate()
    get actionDescriptors() {
      var completed = {
        buttonText: this.isLastStep ? ['_player_next_activity'] : ['_player_go_to_step', { number: this.stepNumber + 1 }],
        actionHandler: this.next
      };
      var showAnswers = {
        buttonText: ['_player_submit_button_show_answer'],
        actionHandler: mediaStoppingHandler(this.completeStep)
      };
      var tryAgain = {
        buttonText: ['_player_submit_button_try_again'],
        actionHandler: mediaStoppingHandler(this.tryAgain)
      };
      var checkAnswer = {
        buttonText: ['_player_submit_button_check_answer'],
        actionHandler: mediaStoppingHandler(this.submitStep)
      };
      var skip = {
        buttonText: ['_player_skip_button'],
        actionHandler: this.next
      };
      var submit = {
        primary: true,
        buttonText: ['_player_submit_button'],
        actionHandler: mediaStoppingHandler(this.submitForTest)
      };
      return { completed: completed, showAnswers: showAnswers, checkAnswer: checkAnswer, skip: skip, tryAgain: tryAgain, submit: submit };
    },

    get initialStepStatus() {
      var activityId = props.activity.activityId,
          course = props.course,
          sequence = props.sequence,
          activityStepId = props.step.activityStepId;

      return {
        courseId: course && course.courseId,
        sequenceId: sequence && sequence.sequenceId,
        version: sequence && sequence.version,
        activityId: activityId,
        activityStepId: activityStepId,
        values: undefined,
        answers: undefined,
        maxFailAttempts: 2,
        review: false,
        ready: false,
        completed: false,
        attempts: 0,
        correct: null,
        skipped: false
      };
    },

    extractDefaultActivitySubStepIdFromStep: function extractDefaultActivitySubStepIdFromStep(step) {
      // pronunciation substeps are a little different from others
      //  because they can have single-member carousels and those aren't considered substeps
      if (pronunciationModel.isPronunciation(step)) {
        if (pronunciationModel.isMultiplePronunciation(step)) {
          var pronunciationItems = pronunciationModel.getPronunciationMultipleItems(step);
          if (Array.isArray(pronunciationItems) && pronunciationItems.length > 0 && pronunciationItems[0].id) {
            return pronunciationItems[0].id;
          }
        }
      } else {
        var firstCarouselSection = stepModel.getCarouselSection(step, 0);
        if (Array.isArray(firstCarouselSection)) {
          var firstSectionItem = firstCarouselSection.find(function (item) {
            return item.type === 'word' || item.type === 'usage';
          });
          if (firstSectionItem) {
            return firstSectionItem.id;
          }
        }
      }
      return null;
    },
    start: function start() {
      var stepStatus = props.stepStatus,
          activity = props.activity,
          step = props.step;

      var activityStepAttemptId = uuidV4();
      var activityStepAttemptStartTime = new Date().getTime();
      var initialStepStatus = this.initialStepStatus;
      var stepNumber = _.indexOf(activity.steps, step) + 1;
      var activityAttemptId = stepStatus.activityAttemptId;
      // if they restart an activity without going to another activity
      //  we still want a new activityAttemptId
      if (!activityAttemptId || activity.activityId !== stepStatus.activityId || stepNumber === 1) {
        // Keep the same activityAttemptId if the previous step was in the same activity.
        activityAttemptId = uuidV4();
      }
      var stepsCount = _.size(activity.steps);
      var startMessage = _extends({
        activityAttemptId: activityAttemptId,
        activityStepAttemptId: activityStepAttemptId,
        activitySubStepId: this.extractDefaultActivitySubStepIdFromStep(step),
        activityStepAttemptStartTime: activityStepAttemptStartTime,
        stepNumber: stepNumber,
        stepsCount: stepsCount
      }, initialStepStatus);

      stepStatus.startActivityStep(startMessage);
      // seems that assessments don't have a course or a sequence visible to the client,
      //   just a string of activities - so we test for props.course before dereference it
      if (props.course && stepStatusModel.isActivityStart({ stepNumber: stepNumber, completed: initialStepStatus.completed })) {
        amplitudeHelper.sendAmplitudeEvent(amplitudeHelper.analyticEventNames.ACTIVITY_STARTED, {
          courseId: props.course.courseId,
          lessonId: props.sequence.sequenceId,
          lessonVersion: props.sequence.version,
          activityId: props.activity.activityId
        });
      }
    },
    submitStep: function submitStep() {
      var payload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var _props$stepStatus = props.stepStatus,
          submitActivityStep = _props$stepStatus.submitActivityStep,
          attempts = _props$stepStatus.attempts;


      var interfaceLanguage = userSelectors.getUILanguage(props.reduxStore.getState());

      submitActivityStep(_extends({
        attempts: attempts + 1,
        review: true,
        completed: !!payload.correct,
        interfaceLanguage: interfaceLanguage,
        courseProgressUpdated: function courseProgressUpdated(progressMessage, progressBefore, progressAfter) {
          props.reduxStore.dispatch({
            type: COURSE_PROGRESS_UPDATED,
            payload: { progressMessage: progressMessage, progressBefore: progressBefore, progressAfter: progressAfter }
          });
        }
      }, payload));
    },
    submitForTest: function submitForTest(payload) {
      this.submitStep(payload);
      this.next();
    },
    updateStep: function updateStep(payload) {
      var updateStepStatus = props.stepStatus.updateStepStatus;

      updateStepStatus(_extends({}, payload));
    },
    skipStep: function skipStep() {
      var payload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var _props$stepStatus2 = props.stepStatus,
          attempts = _props$stepStatus2.attempts,
          skipActivityStep = _props$stepStatus2.skipActivityStep;
      // Skip shouldn't affect attempts
      // TODO - make submit action not related to the redux

      skipActivityStep(_extends({ attempts: attempts, skipped: true }, payload));
      this.nextActivity();
    },
    nextActivity: function nextActivity() {
      var route = props.route,
          history = props.history;
      var paths = route.paths;

      props.stepStatus.activityStepDismissed();
      history.push(paths.nextActivity || paths.summary);
    },
    next: function next() {
      var route = props.route,
          history = props.history;
      var paths = route.paths;

      props.stepStatus.activityStepDismissed();
      if (props.isTestSequence) {
        history.replace(paths.nextStep || paths.summary);
      } else {
        history.push(paths.nextStep || paths.summary);
      }
    },
    tryAgain: function tryAgain() {
      var payload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var _props$stepStatus3 = props.stepStatus,
          updateStepStatus = _props$stepStatus3.updateStepStatus,
          attempts = _props$stepStatus3.attempts;
      // Resetting step status values

      var activityStepAttemptId = uuidV4();
      var activityStepAttemptStartTime = new Date().getTime();

      props.stepStatus.activityStepTryAgainSelected();

      updateStepStatus(_extends({}, this.initialStepStatus, {
        attempts: attempts,
        activityStepAttemptId: activityStepAttemptId,
        activityStepAttemptStartTime: activityStepAttemptStartTime
      }, payload));
    },
    completeStep: function completeStep() {
      var payload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var updateStepStatus = props.stepStatus.updateStepStatus;

      updateStepStatus(_extends({ completed: true }, payload));
    }
  };
});